import React, { useMemo, useRef } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import InformationIcon from "../../assets/icons/info/information.png";

dayjs.extend(weekOfYear);
dayjs.extend(quarterOfYear);

interface IDataPoint {
  period: string;
  date: string;
  cumulative_droppers: number;
  cumulative_droppers_plastic: number;
  cumulative_active_plastic: number;
  percentage_plastic_over_total: number;
  week_start?: string;
  year?: number;
  quarter?: string;
}

interface Props {
  data: IDataPoint[] | null;
  isLoading: boolean;
  period: "weekly" | "monthly" | "quarterly" | "yearly";
  setPeriod: (p: "weekly" | "monthly" | "quarterly" | "yearly") => void;
  year: number;
  setYear: (y: number) => void;
  fromMonth: number;
  setFromMonth: (m: number) => void;
  toMonth: number;
  setToMonth: (m: number) => void;
  fromYear: number;
  setFromYear: (y: number) => void;
  fromWeek: number;
  setFromWeek: (w: number) => void;
  toYear: number;
  setToYear: (y: number) => void;
  toWeek: number;
  setToWeek: (w: number) => void;
}

export const PlasticCumulativeNested: React.FC<Props> = ({
  data,
  isLoading,
  period,
  setPeriod,
  year,
  setYear,
  fromMonth,
  setFromMonth,
  toMonth,
  setToMonth,
  fromYear,
  setFromYear,
  fromWeek,
  setFromWeek,
  toYear,
  setToYear,
  toWeek,
  setToWeek,
}) => {
  const snapshotRef = useRef<HTMLDivElement | null>(null);
  const [, takeScreenshot] = useScreenshot();

  const downloadScreenshot = () => {
    if (!snapshotRef.current) return;
    takeScreenshot(snapshotRef.current).then((image: any) => {
      const link = document.createElement("a");
      link.href = image;
      link.download = "plastic_cumulative_nested.png";
      link.click();
    });
  };

  // Colors aligned with Droppers palette
  const colors = {
    total: "#E6E1FF", // Total Droppers
    plastic: "#FFA486", // Plastic Droppers
    activePlastic: "#FF6E41", // Active Plastic Droppers
    line: "#8E62FA", // Plastic % of Total
  };

  const yearOptions = Array.from(
    { length: dayjs().year() - 2023 },
    (_, i) => dayjs().year() - i
  );
  const fromYearOptions = yearOptions;
  const toYearOptions = yearOptions;
  const monthOptions = useMemo(
    () => [
      { value: 1, label: "Jan" },
      { value: 2, label: "Feb" },
      { value: 3, label: "Mar" },
      { value: 4, label: "Apr" },
      { value: 5, label: "May" },
      { value: 6, label: "Jun" },
      { value: 7, label: "Jul" },
      { value: 8, label: "Aug" },
      { value: 9, label: "Sep" },
      { value: 10, label: "Oct" },
      { value: 11, label: "Nov" },
      { value: 12, label: "Dec" },
    ],
    []
  );
  const fromMonthOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    const cm = new Date().getMonth() + 1;
    return fromYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [fromYear, monthOptions]);
  const toMonthOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    const cm = new Date().getMonth() + 1;
    return toYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [toYear, monthOptions]);

  const getWeekOptions = (y: number) => {
    const last = dayjs(`${y}-12-31`);
    const totalWeeks = last.isoWeek() === 1 ? 52 : last.isoWeek();
    return Array.from({ length: totalWeeks }, (_, i) => {
      const w = i + 1;
      const s = dayjs().year(y).isoWeek(w).startOf("isoWeek");
      return { value: w, label: `Week ${w}: ${s.format("MMM DD")}` };
    });
  };
  const fromWeekOptions = getWeekOptions(fromYear);
  const toWeekOptions = getWeekOptions(toYear);

  const { categories, comboSeries, plasticPct, yAxisMax } = useMemo(() => {
    if (!data || data.length === 0)
      return {
        categories: [],
        comboSeries: [],
        plasticPct: [],
        yAxisMax: 0,
      };

    const cats: string[] = [];
    const totalDroppers: number[] = [];
    const plasticDroppers: number[] = [];
    const activePlasticDroppers: number[] = [];
    const plasticPct: number[] = [];

    data.forEach((p) => {
      let label = "";
      if (period === "weekly" && p.week_start)
        label = dayjs(p.week_start).format("MMM DD");
      else if (period === "monthly") label = dayjs(p.date).format("YYYY-MM");
      else if (period === "quarterly") label = `${p.year} ${p.quarter}`;
      else if (period === "yearly") label = String(p.year);
      cats.push(label);

      // Ensure we're using the exact values from backend (no transformation)
      totalDroppers.push(Number(p.cumulative_droppers) || 0);
      plasticDroppers.push(Number(p.cumulative_droppers_plastic) || 0);
      activePlasticDroppers.push(Number(p.cumulative_active_plastic) || 0);
      plasticPct.push(
        Number((p.percentage_plastic_over_total ?? 0).toFixed(1))
      );
    });

    // Debug: Log the data to console
    if (process.env.NODE_ENV === "development") {
      console.log("PlasticCumulativeNested data:", {
        totalDroppers,
        plasticDroppers,
        activePlasticDroppers,
        plasticPct,
      });
    }

    // Stacked bars: nested structure (smallest at bottom, largest at top)
    // Calculate stacked values:
    // - Bottom: Active Plastic Droppers (smallest)
    // - Middle: Plastic Droppers - Active Plastic Droppers
    // - Top: Total Droppers - Plastic Droppers (largest difference)
    const activePlasticStacked = activePlasticDroppers;
    const plasticMinusActive = plasticDroppers.map((p, i) =>
      Math.max(0, p - (activePlasticDroppers[i] || 0))
    );
    const totalMinusPlastic = totalDroppers.map((t, i) =>
      Math.max(0, t - (plasticDroppers[i] || 0))
    );

    // Calculate max value for y-axis (round to nearest 5000 for clean ticks)
    const maxValue = Math.max(...totalDroppers);
    const yAxisMax = Math.ceil((maxValue * 1.1) / 5000) * 5000; // Round to nearest 5000

    return {
      categories: cats,
      comboSeries: [
        {
          name: "Active Plastic Droppers",
          type: "column",
          data: activePlasticStacked, // Bottom layer (smallest)
        },
        {
          name: "Plastic Droppers (excl. Active)",
          type: "column",
          data: plasticMinusActive, // Middle layer
        },
        {
          name: "Total Droppers (excl. Plastic)",
          type: "column",
          data: totalMinusPlastic, // Top layer (largest)
        },
      ],
      plasticPct, // Keep for tooltip
      yAxisMax, // For y-axis max value
    };
  }, [data, period]);

  const comboOptions: ApexOptions = useMemo(
    () =>
      ({
        chart: {
          id: "plastic-cumulative-nested-combo",
          stacked: true,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
        },
        title: {
          align: "left",
          style: { fontSize: "20px", fontWeight: 600 },
        },
        colors: [colors.activePlastic, colors.plastic, colors.total], // Reversed order for stacked (smallest to largest)
        plotOptions: {
          bar: {
            columnWidth: "40%",
            dataLabels: {
              position: "bottom",
            },
          },
        },
        stroke: { width: [0, 0, 0] },
        dataLabels: {
          enabled: true,
          formatter: function (val: number, opts: any) {
            // Only show percentage on the top bar (Total Droppers - index 2, which is the top layer)
            if (
              opts.seriesIndex === 2 &&
              plasticPct &&
              plasticPct[opts.dataPointIndex] !== undefined
            ) {
              return `${plasticPct[opts.dataPointIndex].toFixed(1)}%`;
            }
            return "";
          },
          style: {
            fontSize: "12px",
            fontWeight: 600,
            colors: ["#373d3f"],
          },
          offsetY: 0, // Move label inside the bar (positive value moves it up from bottom)
        },
        fill: { opacity: [1, 1, 1] },
        states: {
          normal: { filter: { type: "none" } },
          hover: { filter: { type: "none" } },
          active: { filter: { type: "none" } },
        },
        xaxis: { categories, labels: { rotate: -45, trim: false } },
        yaxis: {
          min: 0,
          max: yAxisMax,
          title: { text: "Cumulative Droppers" },
          decimalsInFloat: 0,
          forceNiceScale: true,
          tickAmount: 6,
          labels: { formatter: (v) => Number(v).toLocaleString() },
        },
        legend: { position: "bottom" },
        grid: {
          strokeDashArray: 5,
          padding: { left: 10, right: 30, bottom: 10 },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
            // Calculate actual values from stacked series
            // series[0] = Active Plastic Droppers (bottom)
            // series[1] = Plastic Droppers (excl. Active) (middle)
            // series[2] = Total Droppers (excl. Plastic) (top)
            const activePlasticDroppers = series[0][dataPointIndex] || 0;
            const plasticMinusActive = series[1][dataPointIndex] || 0;
            const totalMinusPlastic = series[2][dataPointIndex] || 0;

            // Calculate actual totals
            const plasticDroppers = activePlasticDroppers + plasticMinusActive;
            const totalDroppers =
              activePlasticDroppers + plasticMinusActive + totalMinusPlastic;

            const percentage = (plasticPct && plasticPct[dataPointIndex]) || 0;

            const seriesNames = [
              "Total Droppers",
              "Plastic Droppers",
              "Active Plastic Droppers",
            ];
            const seriesValues = [
              totalDroppers,
              plasticDroppers,
              activePlasticDroppers,
            ];
            const seriesColors = [
              colors.total,
              colors.plastic,
              colors.activePlastic,
            ];

            let tooltipHtml = '<div style="padding: 12px;">';
            tooltipHtml +=
              '<div style="display: flex; flex-direction: column; gap: 8px;">';

            seriesNames.forEach((name, idx) => {
              const value = seriesValues[idx];
              const color = seriesColors[idx];
              tooltipHtml += `
            <div style="display: flex; align-items: center; gap: 8px;">
              <span style="background-color: ${color}; width: 10px; height: 10px; border-radius: 50%; display: inline-block; flex-shrink: 0;"></span>
              <div style="display: flex; flex-direction: column; gap: 2px; flex: 1;">
                <span style="font-size: 13px; font-weight: 600; color: #373d3f; line-height: 1.2;">${name}</span>
                <span style="font-size: 13px; color: #373d3f; line-height: 1.2;">${Number(value).toLocaleString()}</span>
              </div>
            </div>
          `;
            });

            // Add percentage at the end
            tooltipHtml += `
          <div style="display: flex; align-items: center; gap: 8px; margin-top: 4px; padding-top: 8px; border-top: 1px solid #e0e0e0;">
            <span style="font-size: 13px; font-weight: 600; color: #373d3f;">Plastic % of Total: ${percentage.toFixed(1)}%</span>
          </div>
        `;

            tooltipHtml += "</div></div>";

            return tooltipHtml;
          },
        },
      }) as ApexOptions,
    [
      categories,
      plasticPct,
      yAxisMax,
      colors.total,
      colors.plastic,
      colors.activePlastic,
    ]
  );

  return (
    <div className="flex flex-col w-full max-w-full overflow-hidden p-4 sm:p-6 bg-white  border-gray-border drop-shadow-none rounded-xl mb-6">
      <div className="flex flex-col sm:flex-row w-full items-start justify-end">
        <div className="flex flex-col sm:flex-row items-center justify-start sm:justify-end gap-4">
          {/* Period */}
          <div className="flex items-center gap-2">
            <label className="font-satoshi text-sm text-gray-text">
              Period:
            </label>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {period === "weekly"
                    ? "Weekly"
                    : period === "monthly"
                      ? "Monthly"
                      : period === "quarterly"
                        ? "Quarterly"
                        : "Yearly"}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={period === "weekly"}
                  onClick={() => setPeriod("weekly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Weekly
                </MenuItem>
                <MenuItem
                  disabled={period === "monthly"}
                  onClick={() => setPeriod("monthly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Monthly
                </MenuItem>
                <MenuItem
                  disabled={period === "quarterly"}
                  onClick={() => setPeriod("quarterly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Quarterly
                </MenuItem>
                <MenuItem
                  disabled={period === "yearly"}
                  onClick={() => setPeriod("yearly")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Yearly
                </MenuItem>
              </MenuList>
            </Menu>
          </div>

          {/* Monthly filters */}
          {period === "monthly" && (
            <>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  From:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromYearOptions.map((y) => (
                      <MenuItem
                        key={y}
                        disabled={y === fromYear}
                        onClick={() => setFromYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromMonthOptions.find((m) => m.value === fromMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromMonthOptions.map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={fromMonth === m.value}
                        onClick={() => setFromMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toYearOptions.map((y) => (
                      <MenuItem
                        key={y}
                        disabled={y === toYear}
                        onClick={() => setToYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toMonthOptions.find((m) => m.value === toMonth)?.label ||
                        "Month"}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toMonthOptions.map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={toMonth === m.value}
                        onClick={() => setToMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            </>
          )}

          {/* Weekly filters */}
          {period === "weekly" && (
            <>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  From:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromYearOptions.map((y) => (
                      <MenuItem
                        key={y}
                        disabled={y === fromYear}
                        onClick={() => setFromYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis">
                      {fromWeekOptions.find((w) => w.value === fromWeek)
                        ?.label || "Week"}
                      <ChevronDown className="ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {fromWeekOptions.map((w) => (
                      <MenuItem
                        key={w.value}
                        disabled={fromWeek === w.value}
                        onClick={() => setFromWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toYearOptions.map((y) => (
                      <MenuItem
                        key={y}
                        disabled={y === toYear}
                        onClick={() => setToYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis">
                      {toWeekOptions.find((w) => w.value === toWeek)?.label ||
                        "Week"}
                      <ChevronDown className="ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {toWeekOptions.map((w) => (
                      <MenuItem
                        key={w.value}
                        disabled={toWeek === w.value}
                        onClick={() => setToWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            </>
          )}

          {/* Quarterly */}
          {period === "quarterly" && (
            <div className="flex items-center gap-2">
              <label className="font-satoshi text-sm text-gray-text">
                Year:
              </label>
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {year}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {yearOptions.map((y) => (
                    <MenuItem
                      key={y}
                      disabled={y === year}
                      onClick={() => setYear(y)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </div>
          )}

          {/* Yearly */}
          {period === "yearly" && (
            <>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  From:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {fromYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {fromYearOptions.map((y) => (
                      <MenuItem
                        key={y}
                        disabled={y === fromYear}
                        onClick={() => setFromYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
              <div className="flex items-center gap-2">
                <label className="font-satoshi text-sm text-gray-text">
                  To:
                </label>
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                      {toYear}
                      <ChevronDown className="ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {toYearOptions.map((y) => (
                      <MenuItem
                        key={y}
                        disabled={y === toYear}
                        onClick={() => setToYear(y)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {y}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </div>
            </>
          )}

          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
          />
        </div>
      </div>

      <section ref={snapshotRef}>
        {" "}
        <div className="flex items-center mb-2 mt-4">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
            Cumulative Plastic Conversion
          </p>
          <Tooltip
            content="Overlapping bars: Plastic subsets of Total. Line = Plastic %"
            placeholder={undefined}
          >
            <img
              src={InformationIcon}
              alt="info"
              className="inline-block w-4 h-4 ml-2 cursor-pointer"
            />
          </Tooltip>
        </div>
        <div>
          {isLoading ? (
            <div className="flex items-center justify-center h-[420px]">
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : !data || data.length === 0 ? (
            <div className="flex items-center justify-center h-[420px]">
              <p className="text-gray-500">No data available</p>
            </div>
          ) : (
            <Chart
              key={`combo-${period}-${categories.length}`}
              type="bar"
              height={450}
              options={comboOptions}
              series={comboSeries as any}
            />
          )}
        </div>
      </section>
    </div>
  );
};
